import React, { useState } from 'react';
import { api } from '../api';
import { Input, Textarea, Button, useToast, T } from '../components/UI';

const SUBJECTS = ['General Enquiry','About a Specific Car','Import Service','Finance','Part Exchange','Other'];
const WA = '353851234567';

export default function Contact() {
  const { show, ToastContainer } = useToast();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', phone:'', subject:'', message:'' });
  const [errors, setErrors] = useState({});

  const set = k => e => { setForm(f => ({ ...f, [k]: e.target.value })); setErrors(er => ({ ...er, [k]:'' })); };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Valid email required';
    if (!form.message.trim() || form.message.length < 10) errs.message = 'Please add a message';
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try { await api.sendEnquiry({ ...form, type: 'general' }); setSent(true); }
    catch { show('Failed to send. Please call us.', 'error'); }
    setLoading(false);
  };

  return (
    <div style={{ paddingTop: 68 }}>
      <ToastContainer />

      {/* Hero */}
      <div style={{ background: T.ink, paddingBlock: 'clamp(48px,8vw,96px)', paddingInline: 'var(--margin)', position: 'relative', overflow: 'hidden' }}>
        <img src="https://picsum.photos/id/85/1920/700" alt="" loading="lazy" width="1920" height="700"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.12, display: 'block' }} />
        <div style={{ maxWidth: 1680, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <p style={{ fontSize: 'var(--step-xs)', color: T.accent, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>Get in touch</p>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2.5rem,5vw,4.5rem)', fontWeight: 400, color: T.bg, lineHeight: 1.08, letterSpacing: '-0.02em', maxWidth: '16ch' }}>
            We'd love to<br/>hear from you.
          </h1>
        </div>
      </div>

      {/* Quick channels strip */}
      <div style={{ background: T.accent }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderLeft: '1px solid rgba(255,255,255,0.2)' }}>
          {[
            { icon:'📞', label:'Call us',   value:'+353 85 123 4567', sub:'Mon–Sat 9am–6pm',         href:'tel:+353851234567' },
            { icon:'💬', label:'WhatsApp',  value:'Message us now',  sub:'Reply within 1 hour',       href:`https://wa.me/${WA}` },
            { icon:'✉️', label:'Email',     value:'info@northbridgeauto.ie', sub:'Same-day reply',  href:'mailto:info@northbridgeauto.ie' },
          ].map(item => (
            <a key={item.label} href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '22px 28px', borderRight: '1px solid rgba(255,255,255,0.2)', color: '#fff', textDecoration: 'none', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontSize: 24 }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: 'var(--step-xs)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.75, marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-sm)', fontWeight: 400 }}>{item.value}</div>
                <div style={{ fontSize: 'var(--step-xs)', opacity: 0.65, marginTop: 1 }}>{item.sub}</div>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Form + info */}
      <div style={{ paddingBlock: 'clamp(60px,8vw,100px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 360px', gap: 60, alignItems: 'start' }}>

          {/* Form */}
          <div>
            <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>Send a message</p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.75rem,3.5vw,3rem)', fontWeight: 400, letterSpacing: '-0.02em', marginBottom: 32 }}>How can we help?</h2>

            {sent ? (
              <div style={{ border: `1px solid ${T.accent}`, padding: '48px 40px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2.5rem,5vw,4rem)', color: T.accent, marginBottom: 16, lineHeight: 1 }}>✓</div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'var(--step-head)', fontWeight: 400, marginBottom: 12 }}>Message sent</h3>
                <p style={{ fontSize: 'var(--step-body)', color: T.muted, lineHeight: 1.7, maxWidth: '36ch', margin: '0 auto 28px' }}>
                  Thanks <strong style={{ color: T.ink }}>{form.name}</strong>. We'll get back to you shortly.
                </p>
                <Button variant="outline" onClick={() => { setSent(false); setForm({ name:'', email:'', phone:'', subject:'', message:'' }); }}>Send another message</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Input label="Full name *" placeholder="Seán Murphy" value={form.name} onChange={set('name')} error={errors.name} />
                    <Input label="Phone" placeholder="+353 85 …" value={form.phone} onChange={set('phone')} type="tel" />
                  </div>
                  <Input label="Email *" placeholder="you@example.com" value={form.email} onChange={set('email')} type="email" error={errors.email} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Subject</label>
                    <div style={{ position: 'relative' }}>
                      <select value={form.subject} onChange={set('subject')}
                        style={{ width: '100%', border: `1px solid ${T.surface}`, background: T.bg, padding: '11px 36px 11px 14px', fontSize: 'var(--step-body)', color: T.ink, appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', outline: 'none' }}
                        onFocus={e => e.target.style.borderColor = T.accent}
                        onBlur={e => e.target.style.borderColor = T.surface}>
                        <option value="">Select subject</option>
                        {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                      </select>
                      <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 10 }}>▼</span>
                    </div>
                  </div>
                  <Textarea label="Message *" placeholder="Tell us what you're looking for, or ask any question…" value={form.message} onChange={set('message')} error={errors.message} style={{ minHeight: 150 }} />
                  <Button type="submit" size="lg" fullWidth loading={loading}>Send message</Button>
                  <p style={{ fontSize: 'var(--step-xs)', color: T.muted, textAlign: 'center', letterSpacing: '0.04em' }}>Your details will never be shared.</p>
                </div>
              </form>
            )}
          </div>

          {/* Info sidebar */}
          <div style={{ position: 'sticky', top: 90 }}>
            {/* Contact details */}
            <div style={{ background: T.surface, padding: '28px 24px', marginBottom: 2 }}>
              <p style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>Contact details</p>
              {[
                { icon:'📞', label:'Phone',    value:'+353 85 123 4567',       href:'tel:+353851234567' },
                { icon:'✉️', label:'Email',    value:'info@northbridgeauto.ie', href:'mailto:info@northbridgeauto.ie' },
                { icon:'📍', label:'Location', value:'Dublin, Ireland',         href: null },
                { icon:'🕐', label:'Hours',    value:'Mon–Sat: 9am–6pm\nSunday: By appointment', href: null },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', gap: 14, paddingBlock: 14, borderBottom: `1px solid ${T.bg}` }}>
                  <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>{item.label}</div>
                    {item.href ? (
                      <a href={item.href} style={{ fontSize: 'var(--step-sm)', fontWeight: 500, color: T.ink, whiteSpace: 'pre-line', transition: 'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color = T.accent}
                        onMouseLeave={e => e.currentTarget.style.color = T.ink}>{item.value}</a>
                    ) : (
                      <div style={{ fontSize: 'var(--step-sm)', fontWeight: 500, color: T.ink, whiteSpace: 'pre-line' }}>{item.value}</div>
                    )}
                  </div>
                </div>
              ))}
              <a href={`https://wa.me/${WA}`} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20, background: '#25D366', color: '#fff', padding: '13px', fontSize: 'var(--step-xs)', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', transition: 'opacity 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.532 5.855L.054 23.946l6.283-1.649A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm5.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
                Chat on WhatsApp
              </a>
            </div>

            {/* Response time */}
            <div style={{ background: T.ink, padding: '22px 24px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <span style={{ color: T.accent, fontSize: 20, flexShrink: 0 }}>⚡</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 'var(--step-sm)', color: T.bg, marginBottom: 6 }}>Fast response</div>
                <div style={{ fontSize: 'var(--step-xs)', color: 'rgba(232,230,225,0.55)', lineHeight: 1.6, letterSpacing: '0.02em' }}>
                  We typically reply to all enquiries within 1–2 hours during business hours. For urgent queries, call or WhatsApp directly.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
